import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  OnDestroy,
  inject,
  ViewChild
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep, isEqual } from 'lodash-es';
import { MessageService } from 'primeng/api';
import { BehaviorSubject, combineLatest, filter, Subject, Subscription, takeUntil } from 'rxjs';
import { ApiConstants } from 'src/app/base/api-constants';
import { UtilService } from 'src/app/services/util.service';
import { v4 as uuidv4 } from 'uuid';
import { tableFieldsForDM } from 'src/app/STORE/selectors/services.selector';
import { DMConstants, nodeType } from 'src/app/pages/designs/data-model/data-model.component';
import { allIndices } from 'src/app/STORE/selectors/indexdesigner.selector';
import { IndexType } from '../index-designer/index-designer.component';
import { GlobalConstants } from 'src/app/base/global.constants';
import { PickupListService } from '../pickup-list/pickup-list.service';
import { picklist } from 'src/app/STORE/selectors/propertypane.selector';
import { ReferenceUtilService } from 'src/app/components/reference-tree-view/reference-util.service';
import { ActionType, ModelType, UiActions } from '../reference-tree-view/reference.enum';
import { AppGlobalService } from 'src/app/services/app.global.service';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { FsBaseService } from 'src/app/services/fs-base.service';
import {
  deleteLookupDefinitions,
  getLookupDefinitions,
  getLookupDefinitionById,
  saveLookupDefinitions,
  updateLookupDefinitions,
  getLookupDefinitionsSuccess
} from 'src/app/STORE/actions/lookupdefinitions.action';
import {
  deleteLookupDefinitionsSuccessData,
  getLookupDefinitionsByIdSuccessData,
  getLookupDefinitionsSuccessData
} from 'src/app/STORE/selectors/lookupdefinitions.selector';

@AutoUnsubscribe({ arrayName: 'subscriptions' })
@Component({
  selector: 'vb-lookup-definition',
  templateUrl: './lookup-definition.component.html',
  styleUrls: ['./lookup-definition.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [],
  standalone: false //NOSONAR
})
export class LookupDefinitionComponent implements OnInit, OnDestroy {
  private confirmationDialogService = inject(ConfirmationDialogService);
  private onChange: (m: any) => void;
  private onTouched: (m: any) => void;
  private store = inject(Store);
  private messageService = inject(MessageService);
  private util = inject(UtilService);
  private appGlobalService = inject(AppGlobalService);
  private translate = inject(TranslateService);
  private pickupListService = inject(PickupListService);
  private referenceUtil = inject(ReferenceUtilService);
  private fsService = inject(FsBaseService);

  @Input() element: any;
  @Input() disableField = false;
  @Input() formData: any;
  @Input() fieldIdMap: any = null;
  @Input() isRDBMS = false;
  @Input() pickList;
  @Input() searchTerm = null;
  lookupTemplates: any = [];
  lookupCloneCopy: any = [];
  displayFieldForQueryView = [];
  fields: any = [];
  selected: string;
  allFields: any[] = [];
  source: any[] = [];
  target: any[] = [];
  showPickList = false;
  selectedPL: any[];
  currentTemplate: any = null;
  svgPaths = GlobalConstants.svgPaths;
  targetBackup: any[] = [];
  indices: any = [];
  private allIndicesCache: any[] = [];
  @Output() public readonly templateChange = new EventEmitter();
  destroy$: Subject<boolean> = new Subject<boolean>();
  showLoader = false;
  fromSave: boolean;
  indexedFields: any = [];
  pickListFrom = '';
  subscriptions: Subscription[] = [];
  allFieldsFiltered: any = [];
  tableName = '';
  initialCall = true;
  fsDataSub: Subscription | null = null;
  activeLookup: any = null;
  activeRow: any = null;
  @ViewChild('popover') popover: any;
  private allFields$ = new BehaviorSubject<any[]>([]);
  private lookupTemplates$ = new BehaviorSubject<any[]>([]);
  excludedDisplayFields = ['file attachments', 'image carousel'];

  get isBetaAccessPrivilegeConsumable(): boolean {
    return this.appGlobalService.betaAccessPrivilegeForApp;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  getTemplateName(index) {
    let templateNameAfterValidation = this.formData.name;
    if (this.formData.name.length > 55) {
      const result = this.formData.name.match(/.{1,55}/g) ?? [];
      templateNameAfterValidation = result[0];
    }
    const templateName = `${templateNameAfterValidation} lookup ${index}`;
    const templateProp = this.lookupTemplates.find((im) => im.name.toLowerCase() === templateName.toLowerCase());
    if (templateProp) {
      return this.getTemplateName(index + 1);
    }
    return templateName;
  }

  getInd() {
    const primInds = this.indices.filter((ind) => ind.indexType === IndexType.PRIMARY);
    if (primInds.length) {
      return primInds[0];
    }
    return this.indices[0];
  }

  addLookupTemplate() {
    const id = uuidv4();
    const lookupName = this.getTemplateName(this.lookupTemplates.length + 1);
    const index = this.getInd();
    let lookupObj = {
      sid: id,
      name: lookupName,
      fields: [],
      inputFilters: index?.fields?.map((inp) => inp)
    };
    if (this.formData.t_type === DMConstants.QUERY_VIEW) {
      if (this.formData?.t_unique_for_lookup) {
        lookupObj = {
          ...lookupObj,
          ...{
            displayFieldForQueryView: this.formData.t_unique_for_lookup.split(',').map((s: string) => s.trim())
          }
        };
      } else {
        this.util.showMessage('error', this.translate.instant('LOOKUP_DEF_WARN'));
        return;
      }
    } else {
      lookupObj = {
        ...lookupObj,
        ...{
          index: index?.sid,
          displayField: index?.fields?.map((idty) => {
            const label = this.getFieldLabel(idty);
            return { referenceField: idty, displayField: { sid: idty, name: label } };
          })
        }
      };
    }
    this.selected = lookupObj.sid;
    this.lookupTemplates = [...this.lookupTemplates, lookupObj];
    const i = this.lookupTemplates.length;
    this.setValueOnChanges(null, lookupObj, 'create');
  }

  setValueOnChanges(event, lookup, action?) {
    if (lookup.name) {
      delete lookup.err;
      let data = this.lookupTemplates
        .filter((l) => l.name && l.sid === lookup.sid)
        .map((lk) => ({
          ...lk,
          displayField: lookup?.displayField?.map((m) => ({
            referenceField: m.referenceField,
            displayField: m.displayField?.sid ? m.displayField.sid : m.displayField
          })),
          inputFilters: lookup?.inputFilters?.map((m) => (m.sid ? m.sid : m))
        }))[0];

      this.lookupCloneCopy = this.lookupTemplates.filter((l) => l.name);
      const {
        invalidReferencedFieldMsg,
        invalidDisplayFieldMsg,
        invalidInputFilterFieldMsg,
        invalidOutputFilterFieldMsg,
        ...cleanedData
      } = data;
      data = cleanedData;
      data = {
        ...data,
        tableId: this.formData.sid,
        orgId: this.appGlobalService.get('orgId'),
        appId: this.appGlobalService.get('appId')
      };

      if (action === 'create') {
        this.store.dispatch(
          saveLookupDefinitions({
            apiConstants: ApiConstants.saveLookupDefinitions,
            payload: data
          })
        );
      } else if (action === 'delete') {
        this.store.dispatch(
          deleteLookupDefinitions({
            apiConstants: ApiConstants.deleteLookupDefinitions,
            lookupdefinitionId: lookup.sid
          })
        );
      } else {
        this.store.dispatch(
          updateLookupDefinitions({
            apiConstants: ApiConstants.updateLookupDefinitions,
            payload: data
          })
        );
      }
    }
  }

  getFieldLabel(field: string | { sid?: string; name?: string; altName?: string } | null | undefined): string {
    if (!field) {
      return '';
    }

    if (typeof field === 'object') {
      if (field.altName) {
        return field.altName;
      }
      if (field.name) {
        return field.name;
      }
    }

    const fieldSid = typeof field === 'string' ? field : field.sid;
    if (!fieldSid) {
      return '';
    }

    return this.fieldIdMap?.[fieldSid] || this.allFields.find((candidate) => candidate.sid === fieldSid)?.name || fieldSid;
  }

  showFilteredOptions(fld) {
    
    this.allFieldsFiltered = this.allFields
      .filter((f) => !this.excludedDisplayFields.includes(f.f_type?.toLowerCase()))
      .map((ff) => {
        return { sid: ff.sid, name: ff.name };
      });
  }

  confirmDeletePrompt(i) {
    this.confirmationDialogService.confirm({
      message: this.translate.instant('CONFIRMATION_MESSAGE_DELETE_LOOKUP'),
      header: 'Confirmation',
      accept: () => {
        this.confirmDelete(i);
      },
      reject: () => {
        // Do nothing on reject
      }
    });
  }

  delLookup(event, i) {
    event.stopPropagation();
    if (i > -1) {
      this.trigerGetRef('', UiActions.DELETE, this.lookupTemplates[i], event, i);
    }
  }

  confirmDelete(i) {
    if (i > -1) {
      const lookup = this.lookupTemplates[i];
      this.setValueOnChanges(null, lookup, 'delete');
    }
  }

  setValueToPickList(lookupTemp, field: string) {
    const value = lookupTemp[field];
    this.selectedPL = [];
    this.target = [];
    let srcFields: any;
    if (field === 'inputFilters') {
      const selectedIndex = this.indices.filter((i) => i.sid === lookupTemp.index.sid);
      const fields = selectedIndex[0].fields?.map((o) => {
        const label = this.getFieldLabel(o);
        return { sid: o, name: label, searchValue: label };
      });
      srcFields = fields;
    } else {
      // srcFields = this.allFields;
      srcFields = this.allFields.filter((obj) => !lookupTemp.inputFilters.includes(obj.sid));
    }
    if (value.length > 0) {
      value.forEach((e) => {
        if (e) {
          const label = this.getFieldLabel(e);
          this.target.push({ sid: e, name: label, searchValue: label });
        }
      });
      this.source = srcFields
        .filter((fld) => !this.target.find((f) => f.sid === fld.sid))
        .map((obj) => {
          const label = this.getFieldLabel(obj);
          return { sid: obj.sid, name: label, searchValue: label };
        });
    } else {
      this.source = srcFields.map((obj) => {
        const label = this.getFieldLabel(obj);
        return { sid: obj.sid, name: label, searchValue: label };
      });
      this.target = [];
    }
    if (!this.target) {
      this.target = [];
    }
    this.selectedPL = [...this.target];
  }

  togglePickList(event?: any, lookTem?: any, field?: string) {
    this.pickListFrom = field ?? '';
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      this.setValueToPickList(lookTem, field ?? '');
      this.target = [...this.selectedPL];
      this.targetBackup = [...this.target];
      this.currentTemplate = lookTem;
    } else {
      this.target = [];
      this.source = [];
      this.selectedPL = [];
    }
    this.showPickList = !this.showPickList;
  }

  callSavePicklist() {
    this.selectedPL = this.target.map((ele) => {
      const obj = { ...ele };
      delete obj.allowEdit;
      if (Object.prototype.hasOwnProperty.call(ele, 'edit')) {
        delete obj.edit;
      }
      if (Object.prototype.hasOwnProperty.call(ele, 'name')) {
        delete obj.name;
      }
      if (Object.prototype.hasOwnProperty.call(ele, 'err')) {
        delete obj.err;
      }
      return obj.sid;
    });
    this.currentTemplate = { ...this.currentTemplate, ...{ [this.pickListFrom]: this.selectedPL } };
    this.updateLookupArr(this.currentTemplate);
    this.setValueOnChanges(null, this.currentTemplate);
    this.fromSave = true;
    this.togglePickList(null, null);
    this.currentTemplate = null;
  }

  showMessage(status: string, message: string, detail?: string) {
    this.messageService.clear();
    this.messageService.add({
      life: 10000,
      severity: status,
      summary: message,
      detail: detail
    });
  }

  closeDialog(event) {
    if (!isEqual(this.target, this.targetBackup) && !this.fromSave) {
      this.confirmationDialogService.confirm({
        message: 'You have unsaved changes. Are you sure you want to leave this page?',
        accept: () => {
          this.target = [...this.targetBackup];
          this.showPickList = false;
        },
        reject: () => {
          this.showPickList = true;
        }
      });
    } else {
      this.showPickList = false;
    }
    this.fromSave = false;
  }

  updateLookupArr(lookup) {
    const updLookups = this.lookupTemplates.map((data) => {
      return data.sid === lookup.sid ? lookup : data;
    });
    this.lookupTemplates = [...updLookups];
  }

  findField(sid, node, fld) {
    if (!node) {
      return [];
    }
    const res = node.find((nd) => {
      if (!nd.title) {
        return false;
      }
      if (nd.type === nodeType.FIELD && nd.data.sid === sid) {
        fld = nd.data;
        return true;
      } else if (nd.children) {
        return this.findField(sid, nd.children, fld);
      }
    });
    return res;
  }

  ngOnInit(): void {
    this.tableName = this.formData.name;
    this.store
      .select(tableFieldsForDM)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        if (res) {
          if (res.length > 0) {
            // && !fld.f_sysF

            this.allFields = res.filter(
              (fld) =>
                fld.f_type !== 'Image Carousel' &&
                fld.f_type !== 'File Attachments' &&
                fld.f_type !== 'Complex Datatype' &&
                fld.f_type !== 'Lookup' &&
                !fld.f_transientField &&
                fld.name !== 'Sync Time'
            );
            this.allFields$.next(this.allFields);
          }
        }
      });
    if (this.formData.t_type === DMConstants.TRANS_ONLY || this.formData.t_type === DMConstants.TRANS_ANALY) {
      this.searchIndexes();
    }
    if (this.formData.t_type === 'query_view') {
      this.setOptionsForQueryView();
    }
    const pickSub = this.store.select(picklist).subscribe((pickList) => {
      if (pickList) {
        this.pickList = pickList;
        this.setOptionsForQueryView();
      }
    });
    this.addToSubscriptionsList(pickSub);
    const deleteSuccessSub = this.store.select(deleteLookupDefinitionsSuccessData).subscribe((response) => {
      if (response) {
        this.lookupTemplates = this.lookupTemplates.filter((lk) => lk.sid !== response);
      }
    });
    this.addToSubscriptionsList(deleteSuccessSub);

    this.store
      .select(allIndices)
      .pipe(takeUntil(this.destroy$))
      .subscribe((indices: any) => {
        if (indices && indices.length > 0) {
          this.allIndicesCache = indices;
          this.indices = indices.filter((o) => o.indexType !== 'SECONDARY');
        }
      });

    this.getDataFromFS();
    combineLatest([this.allFields$, this.lookupTemplates$])
      .pipe(filter(([fields, templates]) => fields.length > 0 && templates.length > 0))
      .subscribe(([fields, templates]) => this.validateFieldsinLookUp());
  }

  setOptionsForQueryView() {
    if (this.pickList) {
      // only for table properties lookup field
      this.displayFieldForQueryView = this.pickList?.uniqueFields
        ? this.pickList?.uniqueFields.map((fld) => {
            return {
              label: fld.name,
              value: fld.sid
            };
          })
        : [];
      this.fields = this.pickList?.uniqueFields
        ? this.pickList?.uniqueFields.map((fld) => {
            return {
              label: fld.name,
              value: fld.sid
            };
          })
        : [];
    }
  }
  searchIndexes(event?) {
    const query = event?.query?.toLowerCase();
    let list = this.allIndicesCache.filter((o) => o.indexType !== 'SECONDARY');
    if (query) {
      list = list.filter((f: any) => f.name?.toLowerCase().includes(query));
    }
    this.indices = list;
  }

  changeDF(event) {
    console.log(this.lookupTemplates);
  }

  searchIndexedFields(event, data) {
    this.indexedFields = data.index.fields.map((d) => {
      return { sid: d, name: this.getFieldLabel(d) };
    });
  }

  searchLookupFields(event, data) {
    const dFields = data.index.fields.map((d) => {
      return { sid: d, name: this.getFieldLabel(d) };
    });
    this.fields = data.fields.map((obj) => {
      return { sid: obj.sid, name: this.getFieldLabel(obj) };
    });
    dFields.forEach((o: any) => this.fields.unshift(o));
  }

  updateSelectedIndex(event, data) {
    const inObj = this.indices.find((ind) => ind.sid === data.index);
    const updatedData = {
      ...data,
      displayField: inObj?.fields.map((id) => {
        const label = this.getFieldLabel(id);
        return { referenceField: id, displayField: { sid: id, name: label } };
      }),
      inputFilters: inObj?.fields.map((id) => id),
      fields: []
    };
    this.setValueOnChanges(event, updatedData);
  }

  onUnSelect(event, data) {
    data.displayField = [];
    data.inputFilters = [];
    this.setValueOnChanges(event, data);
  }

  onNameChange(event, lookup) {
    if (!lookup.name) {
      this.handleEmptyName(lookup);
      return;
    }

    // Trim the lookup name to remove leading and trailing whitespace
    lookup.name = lookup.name.trim();

    const validationResult = this.validateLookupName(lookup);
    if (!validationResult.isValid) {
      this.showMessage('error', validationResult.errorMessage);
      this.revertLookupName(lookup);
      return;
    }

    this.trigerGetRef('NAME_UPDATE', 'UPDATE', lookup, event);
  }

  private validateLookupName(lookup: any): { isValid: boolean; errorMessage: string } {
    const myRe = /^[a-zA-Z]+[a-zA-Z0-9 _]*$/; //NOSONAR
    const array = myRe.exec(lookup.name);

    if (!array) {
      return { isValid: false, errorMessage: this.translate.instant('ALPHANUMEREIC_CHARACTERS_ALLOWED') };
    }

    const isDuplicate = this.lookupTemplates.some(
      (ind: any) => ind.sid !== lookup.sid && ind.name.toLowerCase().trim() === lookup.name.toLowerCase().trim()
    );

    if (isDuplicate) {
      return { isValid: false, errorMessage: 'Lookup Definition Name should be unique' };
    }

    return { isValid: true, errorMessage: '' };
  }

  private revertLookupName(lookup: any): void {
    const cloneIndex = this.lookupCloneCopy.findIndex((ind: any) => ind.sid === lookup.sid);
    if (cloneIndex > -1 && this.lookupCloneCopy[cloneIndex].name) {
      const items = [...this.lookupTemplates];
      items[cloneIndex].name = this.lookupCloneCopy[cloneIndex].name;
      this.lookupTemplates = [...items];
    }
  }

  private handleEmptyName(lookup: any): void {
    this.showMessage('error', 'Lookup Definition Name is required');

    const cloneIndex = this.lookupCloneCopy.findIndex((ind: any) => ind.sid === lookup.sid);
    if (cloneIndex > -1 && this.lookupCloneCopy[cloneIndex].name) {
      const items = [...this.lookupTemplates];
      items[cloneIndex].name = this.lookupCloneCopy[cloneIndex].name;
      this.lookupTemplates = [...items];
    } else {
      lookup.err = true;
      this.updateLookupArr(lookup);
    }
  }

  onMoveToTarget(event) {
    let items = [...this.target];
    items = items.map((item) => {
      item = { ...item, ...{ allowEdit: true } };
      return item;
    });
    this.target = [...items];
    this.source = [...this.source];
    this.selectedPL = [...this.target];
  }

  onMoveToSource(event) {
    const items = this.source.map((item) => {
      const obj = { ...item };
      delete obj.allowEdit;
      if (Object.prototype.hasOwnProperty.call(item, 'name')) {
        delete obj.name;
      }
      if (Object.prototype.hasOwnProperty.call(item, 'edit')) {
        delete obj.edit;
      }
      if (Object.prototype.hasOwnProperty.call(item, 'altName')) {
        delete obj.altName;
      }
      return obj;
    });
    this.source = [...items];
    this.target = [...this.target];
    this.selectedPL = [...this.target];
  }

  ontargetSelect(event) {
    /* Empty */
  }

  onTargetReorder(event) {
    this.source = [...this.source];
    this.target = [...this.target];
  }

  trigerSaveForNoRef(rawData) {
    switch (rawData.genericAction) {
      case 'DELETE':
        this.confirmDeletePrompt(rawData.index);
        // this.confirmDelete(rawData.index);
        break;
      case 'UPDATE':
        switch (rawData.action) {
          case 'INDEX_UPDATE':
            this.updateSelectedIndex(rawData.event, rawData.data);
            break;
          case 'NAME_UPDATE':
          case 'DIS_UPDATE':
            this.setValueOnChanges(rawData.event, rawData.data);
            break;
          case 'FLDS_UPDATE':
            this.callSavePicklist();
            break;
          default:
            // No action required for unknown action types
            break;
        }
        break;
      default:
    }
  }

  trigerGetRef(action, genericAction, lookup, event?, index?) {
    this.showLoader = true;
    const previous = this.lookupCloneCopy.find((l) => l.sid === lookup?.sid);
    const computedActionType = this.determineLookupActionType(previous, lookup, genericAction, action);
    const d = {
      genericAction,
      event,
      data: lookup,
      action,
      index
    };
    this.referenceUtil.handleWithReferences({
      payload: {
        modelId: lookup.sid,
        modelType: ModelType.LOOKUP_TEMPLATE,
        actionType: computedActionType,
        action: genericAction
      },
      node: { name: lookup.name, type: 'Lookup Definition' },
      onConfirm: () => {
        this.showLoader = false;
        switch (d.genericAction) {
          case 'DELETE':
            this.confirmDelete(d.index);
            break;
          case 'UPDATE':
            switch (d.action) {
              case 'INDEX_UPDATE':
                this.updateSelectedIndex(d.event, d.data);
                break;
              case 'NAME_UPDATE':
              case 'DIS_UPDATE':
                this.setValueOnChanges(d.event, d.data);
                break;
              case 'FLDS_UPDATE':
                this.callSavePicklist();
                break;
              default:
                break;
            }
            break;
          default:
            break;
        }
      },
      onNoReferences: () => {
        this.showLoader = false;
        this.trigerSaveForNoRef(d);
      },
      onCancel: () => {
        this.showLoader = false;
        if (d.genericAction === 'UPDATE') {
          const temp = this.lookupCloneCopy.find((t) => t.sid === d?.data?.sid);
          const updateTemps = this.lookupTemplates.map((d) => {
            return d.sid === temp.sid ? temp : d;
          });
          setTimeout(() => {
            this.lookupTemplates = cloneDeep(updateTemps);
          }, 0);
        }
      },
      onError: () => {
        this.showLoader = false;
        this.trigerSaveForNoRef(d);
      }
    });
  }

  private hasFieldRemoval(prev: any, curr: any, isQueryView: boolean): string | null {
    const toArr = (v: any) => (Array.isArray(v) ? v.slice() : []);
    const removed = (oldA: any[], newA: any[]) => oldA.some((x) => !newA.includes(x));

    const [prevInput, currInput] = [toArr(prev.inputFilters), toArr(curr.inputFilters)];
    const [prevOutput, currOutput] = [toArr(prev.fields), toArr(curr.fields)];
    const [prevDisp, currDisp] = [toArr(prev.displayField), toArr(curr.displayField)];
    const [prevQueryDisp, currQueryDisp] = [toArr(prev.displayFieldForQueryView), toArr(curr.displayFieldForQueryView)];

    const mapParents = (list: any[]) => list.map((d: any) => d?.referenceField).filter(Boolean);
    const mapChildren = (list: any[]) => list.map((d: any) => d?.displayField?.sid || d?.displayField).filter(Boolean);

    if (isQueryView && removed(prevQueryDisp, currQueryDisp)) return ActionType.LOOKUP_DISPLAY_FIELD_FOR_QUERY_REMOVE;
    if (removed(prevOutput, currOutput)) return ActionType.LOOKUP_FIELD_UPDATE;
    if (removed(prevInput, currInput)) return ActionType.LOOKUP_INPUT_FIELD_REMOVE;
    if (removed(mapParents(prevDisp), mapParents(currDisp))) return ActionType.LOOKUP_DISPLAY_PARENT_FIELD_REMOVE;
    if (removed(mapChildren(prevDisp), mapChildren(currDisp))) return ActionType.LOOKUP_DISPLAY_CHILD_FIELD_REMOVE;

    return null;
  }

  private hasTransientFieldInUse(curr: any): boolean {
    const toArr = (v: any) => (Array.isArray(v) ? v.slice() : []);
    const transientInUse = (list: any[]) =>
      list.some((sid) => this.allFields.some((f) => f.sid === sid && f.f_transientField));

    const currInput = toArr(curr.inputFilters);
    const currOutput = toArr(curr.fields);
    const currDisp = toArr(curr.displayField);
    const mapParents = (list: any[]) => list.map((d: any) => d?.referenceField).filter(Boolean);
    const mapChildren = (list: any[]) => list.map((d: any) => d?.displayField?.sid || d?.displayField).filter(Boolean);

    return transientInUse(currInput) || transientInUse(currOutput) ||
           transientInUse(mapParents(currDisp)) || transientInUse(mapChildren(currDisp));
  }

  private determineLookupActionType(prev: any, curr: any, genericAction: string, specificAction: string): string {
    if (genericAction === UiActions.DELETE) return ActionType.LOOKUP_DELETE;
    if (!prev) return ActionType.LOOKUP_FIELD_UPDATE;
    if (specificAction === 'NAME_UPDATE' && prev.name !== curr.name) return ActionType.LOOKUP_RENAMED;
    if (specificAction === 'INDEX_UPDATE' && prev.index !== curr.index) return ActionType.LOOKUP_INDEX_CHANGE;

    const isQueryView = this.formData?.t_type === 'query_view' || this.formData?.t_type === DMConstants.QUERY_VIEW;

    const removalAction = this.hasFieldRemoval(prev, curr, isQueryView);
    if (removalAction) return removalAction;

    if (this.hasTransientFieldInUse(curr)) return ActionType.TRANSIENT_FIELD_IN_LOOKUP;

    return ActionType.LOOKUP_FIELD_UPDATE;
  }

  /**
   * Output fields to be chosen in pages for lookup configuration.
   *
   * @param {string} event - The event which is triggered while changing output fields.
   * @param {string} lookup - The Lookup in which event is triggered.
   * @returns {string} - The transformed string with the first character capitalized.
   *
   */
  onQueryChange(event, lookup) {
    this.setValueOnChanges(event, lookup);
  }

  showWhereUsed(event: Event, i: number): void {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    const lookup = this.lookupTemplates[i];

    this.showLoader = true;
    this.referenceUtil.showWhereUsed(
      {
        id: lookup.sid,
        name: lookup.name,
        type: this.translate.instant('LOOKUP')
      },
      { hideLoader: () => (this.showLoader = false) }
    );
  }

  handleDoubleClick(item: any) {
    if (this.source.includes(item)) {
      this.moveToTarget(item);
    } else if (this.target.includes(item)) {
      this.moveToSource(item);
    }
  }

  moveToTarget(item: any) {
    const index = this.source.indexOf(item);
    if (index > -1) {
      this.source.splice(index, 1);
      this.target.push(item);
    }
    this.onMoveToTarget(item);
  }

  moveToSource(item: any) {
    const index = this.target.indexOf(item);
    if (index > -1) {
      this.target.splice(index, 1);
      this.source.push(item);
    }
    this.onMoveToSource(item);
  }

  addToSubscriptionsList(subName) {
    this.subscriptions.push(subName);
  }

  getDataFromFS(): void {
    const version = this.appGlobalService.get('appVersion');
    const queryObject = {
      collectionName: `LookupDefinition_${version}`,
      filter: [
        {
          field: 'appId',
          operator: '==',
          value: String(this.appGlobalService.get('appId') || '')
        },
        {
          field: 'tableId',
          operator: '==',
          value: String(this.formData?.sid || '')
        }
      ]
    };
    this.getLookupData(queryObject);
  }

  getLookupData(queryObject: {
    collectionName: string;
    filter: { field: string; operator: string; value: string }[];
  }): void {
    try {
      if (this.fsDataSub && typeof (this.fsDataSub as any).unsubscribe === 'function') {
        (this.fsDataSub as any).unsubscribe();
      }

      this.fsDataSub = this.fsService.fireService(queryObject).subscribe(
        (res: any[]) => {
          if (res && res.length > 0) {
            let templates: any[] = [];

            const mapTemplate = (d: any): any => ({
              sid: d.sid,
              name: d.name,
              displayField: (d.displayField || []).map((field: any) => ({
                referenceField: field.referenceField,
                displayField: field.displayField
                  ? {
                      sid: field.displayField,
                      name: this.fieldIdMap?.[field.displayField] || ''
                    }
                  : null
              })),
              fields: d.fields,
              inputFilters: d.inputFilters,
              displayFieldForQueryView: d.displayFieldForQueryView,
              index: d.index,
              tableId: d.tableId,
              appId: d.appId,
              orgId: d.orgId
            });

            if (this.initialCall) {
              this.initialCall = false;
              const filteredRes = res?.filter(
                (dt: any) => !dt.payload.doc.metadata.fromCache && !dt.payload.doc.data().recDeleted
              );
              templates = filteredRes.map((obj: any) => mapTemplate(obj.payload.doc.data()));
              this.lookupTemplates = cloneDeep(templates);
              this.lookupCloneCopy = cloneDeep(templates);
            } else {
              const updated = cloneDeep(this.lookupTemplates);
              let hasChanges = false;
              res.forEach((dt: any) => {
                const docData = dt.payload.doc.data();
                const sid = docData.sid;
                const idx = updated.findIndex((t) => t.sid === sid);

                if (docData.recDeleted) {
                  const NOT_FOUND = -1;
                  if (idx !== NOT_FOUND) {
                    updated.splice(idx, 1);
                    hasChanges = true;
                  }
                } else if (dt.payload.doc.exists || dt.type === 'added' || dt.type === 'modified') {
                  const newT = mapTemplate(docData);
                  const NOT_FOUND = -1;
                  if (idx !== NOT_FOUND) {
                    updated[idx] = { ...updated[idx], ...newT };
                    hasChanges = true;
                  } else {
                    updated.push(newT);
                    hasChanges = true;
                  }
                }
              });
              if (hasChanges) {
                this.lookupTemplates = updated;
                this.lookupCloneCopy = cloneDeep(updated);
              }
            }
            this.store.dispatch(getLookupDefinitionsSuccess({ payload: cloneDeep(this.lookupTemplates) }));
            this.lookupTemplates$.next(this.lookupTemplates);
          }
        },
        (error) => {
          console.error(error);
        }
      );
      if (this.fsDataSub) {
        this.subscriptions.push(this.fsDataSub);
      }
    } catch (e) {
      console.warn(e);
    }
  }

  errorMsg: string = '';

  showingPopupFor: any = null;

  isPopupVisible(sid: string, data: any): boolean {
    return this.showingPopupFor === `${sid}-${data.referenceField}`;
  }

  isOutputFieldInvalidPopupVisible(lookup: any): boolean {
    const lookupSid = lookup.sid;
    return this.showingPopupFor === `${lookupSid}-OUTPUT_FIELD_INVALID`;
  }

  isOutputFieldInvalid(lookup: any) {
    const lookupSid = lookup.sid;
    const err = this.validationErrors.find(
      (err) => err.lookupSid === lookupSid && err.errorType === 'OUTPUT_FIELD_INVALID'
    );
    return !!err;
  }

  closeWarningPopup() {
    this.showingPopupFor = null;
  }

  toggleWarningPopup(event: Event, lookup: any, data: any): void {
    const key = `${lookup.sid}-${data.referenceField}`;

    if (this.showingPopupFor === key) {
      this.popover.hide();
      this.showingPopupFor = null;
    } else {
      this.activeLookup = lookup;
      this.activeRow = data;
      this.errorMsg = this.getErrorMessage(lookup, data, false);
      this.popover.show(event);
      this.showingPopupFor = key;
    }
  }

  toggleOutputFieldInvalidPopup(lookup: any): void {
    const key = `${lookup.sid}-OUTPUT_FIELD_INVALID`;
    this.showingPopupFor = this.showingPopupFor === key ? null : key;
    this.errorMsg = this.getErrorMessage(lookup, {}, true);
  }

  getErrorMessage(lookup: any, data: any, isOutputField: boolean): string {
    const lookupSid = lookup.sid;
    if (isOutputField) {
      const err = this.validationErrors.find(
        (e) => e.lookupSid === lookupSid && e.errorType === 'OUTPUT_FIELD_INVALID'
      );
      return err?.errorMessage || '';
    } else {
      const err = this.validationErrors.find(
        (e) => e.lookupSid === lookupSid && e.referenceFieldId === data.referenceField
        // e.displayFieldId === data.displayField?.sid
      );
      return err?.errorMessage || '';
    }
  }

  hasError(lookupSid: string, data: any): boolean {
    return this.validationErrors.some(
      (e) =>
        e.lookupSid === lookupSid &&
        e.referenceFieldId === data.referenceField &&
        e.displayFieldId === data.displayField?.sid &&
        e.errorType === 'DELETED_FIELD'
    );
  }

  hasWarning(lookupSid: string, data: any): boolean {
    return this.validationErrors.some(
      (e) =>
        e.lookupSid === lookupSid &&
        e.referenceFieldId === data.referenceField &&
        e.displayFieldId === data.displayField?.sid &&
        e.errorType === 'TYPE_MISMATCH'
    );
  }

  validationErrors: {
    lookupSid: string;
    referenceFieldId: string;
    displayFieldId: string;
    errorType: 'DELETED_FIELD' | 'TYPE_MISMATCH' | 'OUTPUT_FIELD_INVALID';
    errorMessage: string;
  }[] = [];
  validateFieldsinLookUp() {
    this.validationErrors = [];
    const sidSet = new Set(this.allFields.map((f) => f.sid));

    this.lookupTemplates.forEach((lookup) => {
      lookup.displayField.forEach((df) => {
        const displayFieldId = df.displayField?.sid;
        const referencedFieldId = df.referenceField;

        // Case 1: Missing (deleted) display field
        if (!sidSet.has(displayFieldId)) {
          if (!displayFieldId) {
            this.validationErrors.push({
              lookupSid: lookup.sid,
              referenceFieldId: referencedFieldId,
              displayFieldId: displayFieldId,
              errorType: 'DELETED_FIELD',
              errorMessage: this.translate.instant('DISPLAY_FIELD_NULL_IN_LK')
            });
          } else {
            this.util.handleDeletedIds([displayFieldId], false, '', false, '', (message) => {
              const msg = this.translate.instant('DISPLAY_FIELD_REMOVED_IN_LOOKUP', { field: message });

              // Push to validation array
              this.validationErrors.push({
                lookupSid: lookup.sid,
                referenceFieldId: referencedFieldId,
                displayFieldId: displayFieldId,
                errorType: 'DELETED_FIELD',
                errorMessage: msg
              });
            });
          }
        } else {
          const referencedField = this.allFields.find((f) => f.sid === referencedFieldId);
          const displayField = this.allFields.find((f) => f.sid === displayFieldId);

          // Case 2: Type mismatch
          if (!referencedField || !displayField || this.excludedDisplayFields.includes(displayField.f_type?.toLowerCase())) {
            this.util.handleDeletedIds([displayFieldId], false, '', false, '', (message) => {
              const msg = this.translate.instant('DISPLAY_FIELD_TYPE_MISMATCH_IN_LOOKUP', { field: message });

              // Push to validation array
              this.validationErrors.push({
                lookupSid: lookup.sid,
                referenceFieldId: referencedFieldId,
                displayFieldId: displayFieldId,
                errorType: 'TYPE_MISMATCH',
                errorMessage: msg
              });
            });
          }
        }
      });

      //handle invalid output filters
      let invalidOutputFilterFields: string[] = [];
      lookup.fields?.forEach((outputFilterId) => {
        if (!sidSet.has(outputFilterId)) {
          invalidOutputFilterFields.push(outputFilterId);
        }
      });

      this.util.handleDeletedIds(invalidOutputFilterFields, false, '', false, '', (message) => {
        const msg = this.translate.instant('OUTPUT_FIELD_REMOVED_IN_LOOKUP', { field: message });

        this.validationErrors.push({
          lookupSid: lookup.sid,
          referenceFieldId: '',
          displayFieldId: '',
          errorType: 'OUTPUT_FIELD_INVALID',
          errorMessage: msg
        });
      });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  // trackBy functions to optimize *ngFor rendering
  trackByLookup = (_index: number, item: { sid?: string } | null): string | number =>
    item && item.sid ? item.sid : _index;
  trackByField = (_index: number, field: { sid?: string } | string | null): string | number => {
    if (field && typeof field === 'object' && 'sid' in field && field.sid) {
      return field.sid;
    }
    if (typeof field === 'string') {
      return field;
    }
    return _index;
  };
}


